/**
 * «Тихий учитель» — coach style 'quiet' (docs/PATH-V2.md §7.1): the second rung of the help ladder of path games.
 *
 *  - no advice arrows, no turn advice, no quizzes, no praise chatter;
 *  - a DANGER moment: one of the child's own pieces (≥ 3) can be taken by the opponent as a gift (`isGift`, the one
 *    definition of gifts.ts). Said after a short delay (`QUIET_DELAY_MS`, the child may notice it himself), with a red
 *    dot on the piece (the board sign 'danger');
 *  - a GIFT moment: the child has an `isGift` capture. After the same delay an invitation «Посмотри внимательно…» —
 *    without an arrow and without a mark; «Подсказка» then reveals it (`reveal`: the gift marked, the capture shown).
 *    The reveal is journalled as a hint by the game, so that move is no longer the child's own (§3.10);
 *  - at most `QUIET_MAX_LINES` lines a game (moments + reveals), never the same moment twice in a row.
 *
 * Pure: `planQuietMoment` only looks; the caller commits a moment with `commitQuietLine` when it is really said (the
 * child may move before the delay is over). Timers stay with the caller (apps/web gameStore.ts).
 * No generative AI: the words are the recorded pools `v3.path.quiet.*` (@gambit/content lines.quiet.ts), said as 'pathLesson'
 * events with `say: [{ pool, n }]` — they pass the parent's «Тихо», because in a quiet game they are the only help.
 */
import { Chess } from 'chess.js';
import type { BoardAnnotations, CoachEvent, Color, PieceType, Square } from '@gambit/shared';
import { VALUE_PAWNS } from '../../analysis/board.ts';
import { giftMoves } from '../../analysis/gifts.ts';
import { PATH_QUIET_LINES, QUIET_POOL } from '@gambit/content';
import type { Rng } from '../phrase.ts';

/** Lines a quiet game may have at most (§7.1). */
export const QUIET_MAX_LINES = 15;
/** The child gets this long to notice the moment himself (§7.1: ~1.5–2 s). */
export const QUIET_DELAY_MS = 1_800;
/** Own pieces worth at least this much make a danger moment (same bar as `ownHung`). */
export const QUIET_DANGER_MIN_VALUE = 3;

export interface QuietMemory {
  v: 1;
  /** lines said this game */
  lines: number;
  /** key of the last moment said (`kind:square:piece`) */
  lastKey: string | null;
}

export function initialQuietMemory(): QuietMemory {
  return { v: 1, lines: 0, lastKey: null };
}

/** Tolerant reader (resume snapshot). */
export function restoreQuietMemory(value: unknown): QuietMemory {
  if (typeof value !== 'object' || value === null) return initialQuietMemory();
  const v = value as Record<string, unknown>;
  const lines = typeof v.lines === 'number' && Number.isFinite(v.lines) && v.lines >= 0 ? Math.floor(v.lines) : 0;
  return { v: 1, lines, lastKey: typeof v.lastKey === 'string' ? v.lastKey : null };
}

export interface QuietMoment {
  kind: 'danger' | 'gift';
  key: string;
  /** the endangered own piece / the opponent's piece that is a gift */
  square: Square;
  piece: PieceType;
  /** the capture: the opponent's (danger) or the child's (gift) */
  uci: string;
  delayMs: number;
  /** said after the delay; danger: with the red dot; gift: no board marks */
  event: CoachEvent;
  /** the gift revealed by «Подсказка» (null for a danger moment: its mark is already on the board) */
  reveal: CoachEvent | null;
}

export interface QuietPlanArgs {
  /** the child to move */
  fen: string;
  childColor: Color;
  /** the child's ply about to be played (event ids) */
  ply: number;
  address?: 'm' | 'f';
}

// ───────────────────────── words ─────────────────────────

/** The wordings of a quiet pool true for this piece (no `when` = true for any), with their 1-based numbers. */
function wordingsOf(pool: string, piece: PieceType | null): { t: string; n: number }[] {
  const all = PATH_QUIET_LINES[pool] ?? [];
  return all.map((w, i) => ({ t: w.t, n: i + 1, when: w.when })).filter((w) => w.when === undefined || (piece !== null && w.when.includes(piece)));
}

function pick<T>(items: readonly T[], rng: Rng): T {
  return items[Math.min(items.length - 1, Math.floor(rng() * items.length))] as T;
}

/**
 * A quiet line as a 'pathLesson' event (it passes the parent's «Тихо»: in a quiet game it is the only help) with the
 * recorded wording in `say`. Null when the pool has no wording for this piece.
 */
function event(id: string, pool: string, piece: PieceType | null, pose: CoachEvent['pose'], rng: Rng, board?: BoardAnnotations): CoachEvent | null {
  const options = wordingsOf(pool, piece);
  if (options.length === 0) return null;
  const w = pick(options, rng);
  const e: CoachEvent = { id, kind: 'pathLesson', priority: 1, text: w.t, bubbleText: w.t, pose, pauseClock: true, say: [{ pool, n: w.n }] };
  if (board) e.board = board;
  return e;
}

// ───────────────────────── facts ─────────────────────────

function pieceOn(fen: string, square: Square): { type: PieceType; color: Color } | null {
  const rows = (fen.split(' ')[0] ?? '').split('/');
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]);
  const row = rows[8 - rank];
  if (row === undefined || file < 0 || file > 7) return null;
  let f = 0;
  for (const ch of row) {
    if (/\d/.test(ch)) f += Number(ch);
    else {
      if (f === file) {
        const lower = ch.toLowerCase();
        return { type: lower as PieceType, color: ch === lower ? 'b' : 'w' };
      }
      f += 1;
    }
  }
  return null;
}

/** The same placement with the opponent to move (no en passant); null when the child is in check (no such view). */
function flippedView(fen: string): string | null {
  try {
    if (new Chess(fen).inCheck()) return null;
  } catch {
    return null;
  }
  const parts = fen.trim().split(/\s+/);
  if (parts.length < 4) return null;
  parts[1] = parts[1] === 'w' ? 'b' : 'w';
  parts[3] = '-';
  return parts.join(' ');
}

/** The child's pieces (≥ min value) the opponent could take as a gift if he moved now; most valuable first. */
export function endangeredPieces(fen: string, childColor: Color, minValue = QUIET_DANGER_MIN_VALUE): { square: Square; piece: PieceType; uci: string }[] {
  const flipped = flippedView(fen);
  if (!flipped) return [];
  let captures: string[];
  try {
    captures = giftMoves(flipped);
  } catch {
    return [];
  }
  const out = new Map<Square, { square: Square; piece: PieceType; uci: string }>();
  for (const uci of captures) {
    const square = uci.slice(2, 4);
    const p = pieceOn(fen, square);
    if (!p || p.color !== childColor || VALUE_PAWNS[p.type] < minValue || out.has(square)) continue;
    out.set(square, { square, piece: p.type, uci });
  }
  return [...out.values()].sort((a, b) => VALUE_PAWNS[b.piece] - VALUE_PAWNS[a.piece]);
}

/** The child's gift captures (most valuable first). */
export function childGifts(fen: string): { square: Square; piece: PieceType; uci: string }[] {
  let captures: string[];
  try {
    captures = giftMoves(fen);
  } catch {
    return [];
  }
  const out: { square: Square; piece: PieceType; uci: string }[] = [];
  for (const uci of captures) {
    const target = uci.slice(2, 4);
    const p = pieceOn(fen, target);
    // en passant: the passed pawn stands beside the landing square
    if (p) out.push({ square: target, piece: p.type, uci });
    else out.push({ square: `${uci[2]}${uci[1]}`, piece: 'p', uci });
  }
  return out.sort((a, b) => VALUE_PAWNS[b.piece] - VALUE_PAWNS[a.piece]);
}

function sideToMoveColor(fen: string): Color {
  return fen.trim().split(/\s+/)[1] === 'b' ? 'b' : 'w';
}

// ───────────────────────── the planner ─────────────────────────

/**
 * The quiet teacher's moment for the child's turn in `args.fen`, or null (nothing to say, the budget is spent, the
 * same moment was said last time, or it is not the child's turn). The bigger of danger / gift wins; a tie → danger.
 */
export function planQuietMoment(args: QuietPlanArgs, memory: QuietMemory, rng: Rng = Math.random): QuietMoment | null {
  if (memory.lines >= QUIET_MAX_LINES) return null;
  if (sideToMoveColor(args.fen) !== args.childColor) return null;
  const danger = endangeredPieces(args.fen, args.childColor)[0] ?? null;
  const gift = childGifts(args.fen)[0] ?? null;
  const dangerValue = danger ? VALUE_PAWNS[danger.piece] : 0;
  const giftValue = gift ? VALUE_PAWNS[gift.piece] : 0;
  const id = (what: string): string => `quiet-${what}-${args.ply}`;

  if (danger && dangerValue >= giftValue) {
    const key = `danger:${danger.square}:${danger.piece}`;
    if (key === memory.lastKey) return null;
    const board: BoardAnnotations = { arrows: [], highlights: [{ square: danger.square, color: 'red' }] };
    const said = event(id('danger'), QUIET_POOL.danger, danger.piece, 'think', rng, board);
    if (said === null) return null;
    return {
      kind: 'danger',
      key,
      square: danger.square,
      piece: danger.piece,
      uci: danger.uci,
      delayMs: QUIET_DELAY_MS,
      event: said,
      reveal: null,
    };
  }
  if (gift) {
    const key = `gift:${gift.square}:${gift.piece}`;
    if (key === memory.lastKey) return null;
    const board: BoardAnnotations = {
      arrows: [{ from: gift.uci.slice(0, 2), to: gift.uci.slice(2, 4), color: 'green' }],
      highlights: [{ square: gift.square, color: 'green' }],
    };
    const invite = event(id('gift'), QUIET_POOL.gift, null, 'think', rng);
    if (invite === null) return null;
    return {
      kind: 'gift',
      key,
      square: gift.square,
      piece: gift.piece,
      uci: gift.uci,
      delayMs: QUIET_DELAY_MS,
      event: invite,
      reveal: event(id('reveal'), QUIET_POOL.reveal, gift.piece, 'point', rng, board),
    };
  }
  return null;
}

/** A moment (or a reveal) was said: count it; `key` = the moment's key (a reveal keeps the last key). */
export function commitQuietLine(memory: QuietMemory, key?: string): QuietMemory {
  return { v: 1, lines: memory.lines + 1, lastKey: key ?? memory.lastKey };
}

/** May the quiet teacher still say anything this game? */
export function quietBudgetLeft(memory: QuietMemory): number {
  return Math.max(0, QUIET_MAX_LINES - memory.lines);
}
